import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import "../styles/expertDetails.css";
import expertDefaultImg from "../images/expert-default.png";
import DeleteExpert from "../components/ExpertDetails/DeleteExpert";
import Swal from "sweetalert2";
import { UrlContext } from "../contexts/UrlContext";
// import bannerBack from "../images/banner_background.png";
import bannerBack from "../images/eph_cms-banner.jpg";
// import logoWhite from "../images/logo_white.png";
import logoWhite from "../images/eph-logo_10yrs.png";
import CV from "../components/EphProfile/CV";
import ProfilePage from "../components/EphProfile/ProfilePage";
import ProjectsPage from "../components/EphProfile/ProjectsPage";
import InvitationsPage from "../components/EphProfile/InvitationsPage";
import MyProjectPage from "../components/EphProfile/MyProjectPage";
import ExpertAverageRating from "../components/EphProfile/ExpertAverageRating";
import { motion, AnimatePresence } from "framer-motion";
import { DateTime } from "luxon";
import ProfileCompletionChart from "../components/EphProfile/ProfileCompletionChart";
import industryLogo from "../images/default_industry.png";
import { useNavigate } from "react-router-dom";
import { ConsultantContext } from "../contexts/ConsultantContext";
import LogOut from "../components/ExpertDetails/LogOut";
import ForcePasswordChangeModal from "../components/ui/ForcePasswordChangeModal";
import FeedbackWidget from "../components/FeedbackWidget";
import useActivityTracker from "../hooks/useActivityTracker";
import ProfilePicture from "../components/EphProfile/ProfilePicture";

export default function ExpertDetails() {
  useEffect(() => {
    document.title = "Expert Details";
  }, []);
  const { API_URL } = useContext(UrlContext);
  const { tokenId, isTokenValid } = useContext(ConsultantContext);
  const { id } = useParams();
  const navigate = useNavigate();

  // Token validation states
  const [loading, setLoading] = useState(true);

  // Component states
  const [data, setData] = useState(null);
  const [count, setCount] = useState(0);
  const [verified, setVerified] = useState(false);
  const mediaLink = "https://media.licdn.com/";
  const databaseLink = "https://images.expertpowerhouse.com";
  const [experienceData, setExperienceData] = useState([]);
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const [showMenuPanel, setShowMenuPanel] = useState(false);
  const [profilePicturePreview, setProfilePicturePreview] = useState("");
  const [editNameTooltip, setEditNameTooltip] = useState(false);
  const [profileName, setProfileName] = useState("");
  const [editedName, setEditedName] = useState("");
  const [linkedinURL, setLinkedinURL] = useState("");
  const [editedLinkedinURL, setEditedLinkedinURL] = useState("");
  const [isNameEditing, setIsNameEditing] = useState(false);
  const [showNameTooltip, setShowNameTooltip] = useState(false);
  const [nameTooltipTimer, setNameTooltipTimer] = useState(null);

  const tabs = [
    { label: "My Profile", state: "profile" },
    { label: "My Projects", state: "myProjects" },
    { label: "Invitations", state: "invitation" },
    { label: "Projects", state: "projects" },
  ];

  const [activeTab, setActiveTab] = useState("profile");
  const [newInvite, setNewInvite] = useState(null);
  const [averageRating, setAverageRating] = useState(null);
  const [showPwdWarning, setShowPwdWarning] = useState(false);
  const [menuPwdOpen, setMenuPwdOpen] = useState(false);

  // Profile completion states
  const [hasCV, setHasCV] = useState(false);
  const [hasExperience, setHasExperince] = useState(false);
  const [hasSkill, setHasSkill] = useState(false);
  const [hasLanguage, setHasLanguage] = useState(false);
  const [hasIndustry, setHasIndustry] = useState(false);

  // Calling activity tracker hook with 10 minutes time limit
  const getToken = () => {
    try {
      const accessToken = localStorage.getItem("AccessToken");
      return accessToken ? accessToken.trim() : null;
    } catch (err) {
      console.error("Error reading token:", err);
      return null;
    }
  };
  const isOnline = useActivityTracker(300000, getToken);
  // const isOnline = useActivityTracker(20000, getToken);

  // 2. SECOND useEffect: Fetch expert data ONLY after token validation
  useEffect(() => {
    if (!isTokenValid || !tokenId) {
      return; // Don't fetch if token is not valid yet
    }

    const fetchExpert = async () => {
      const token = localStorage.getItem("AccessToken");
      const url = `${API_URL}expert-details/${id}`;

      try {
        const res = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        const data = await res.json();

        // Handle unauthorized, token expired, consultant not found (e.g. email changed / stale token)
        const isAuthError =
          res.status === 401 ||
          res.status === 403 ||
          (res.status === 404 &&
            /consultant not found|user not found/i.test(
              data.text || data.message || ""
            )) ||
          /unauthorized|token|expired|consultant not found/i.test(
            data.text || data.message || ""
          );

        if (isAuthError) {
          localStorage.clear();
          navigate("/login");
          return;
        }

        // Success
        fetchProjects();
        setData(data.expert);
        setProfileName(data.expert.name);
        setLinkedinURL(data.expert.link);
        setVerified(data.expert.verified);

        // If needs-to-be-changed password flag (ntbc) is true, force modal
        const needsPwdChange =
          Boolean(data.expert?.ntbc) || Boolean(localStorage.getItem("ntbc"));
        if (needsPwdChange) {
          setShowPwdWarning(true);
        } else {
          setShowPwdWarning(false);
        }

        const profilePic = data.expert.profilePicture;
        if (!profilePic) {
          setProfilePicturePreview(null);
        } else {
          const isFullURL = profilePic.includes(mediaLink);
          setProfilePicturePreview(
            isFullURL ? profilePic : `${databaseLink}${profilePic}`
          );
        }
      } catch (err) {
        console.error("Network or unexpected error:", err);
        Swal.fire({
          title: "Error",
          text: "Network error or server unreachable",
          icon: "error",
          timer: 2000,
        });
      }
    };

    fetchExpert();
  }, [isTokenValid, tokenId, id, count]); // Only run when token is validated

  // 3. THIRD useEffect: Get expert experience ONLY after token validation
  // useEffect(() => {
  //   if (!isTokenValid || !tokenId) {
  //     return; // Don't fetch if token is not valid yet
  //   }

  //   const getExpertExperience = async () => {
  //     const token = localStorage.getItem("AccessToken");
  //     const requestBody = {
  //       expertID: id,
  //     };

  //     try {
  //       let modifiedUrl = "fetch-expert-experience";
  //       const response = await fetch(`${API_URL}${modifiedUrl}`, {
  //         method: "POST",
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //           "Content-Type": "application/json",
  //         },
  //         body: JSON.stringify(requestBody),
  //       });

  //       const responseData = await response.json();
  //       if (responseData.status === "success") {
  //         setExperienceData(responseData.expertExperience);
  //         setHasExperince(true);
  //       } else if (responseData.status === "error") {
  //         console.error("Error fetching expert experience:", responseData.text);
  //       }
  //     } catch (error) {
  //       console.error("Network or unexpected error:", error);
  //     }
  //   };

  //   getExpertExperience();
  // }, [isTokenValid, tokenId, id]); // Only run when token is validated

  // Function to get new invite count
  const fetchProjects = async () => {
    const token = localStorage.getItem("AccessToken");
    try {
      const response = await fetch(
        `${API_URL}get-all-invited-projects?expertID=${id}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json().catch(() => ({}));

      if (
        !response.ok ||
        data.status === "error" ||
        response.status === 401 ||
        response.status === 403 ||
        response.status === 404
      ) {
        const isAuthErr =
          response.status === 401 ||
          response.status === 403 ||
          (response.status === 404 &&
            /consultant not found|user not found/i.test(
              data.text || data.message || ""
            )) ||
          /unauthorized|token|expired|consultant not found/i.test(
            data.text || data.message || ""
          );

        if (isAuthErr) {
          localStorage.clear();
          navigate("/login");
          return;
        }

        Swal.fire({
          title: "Couldn't fetch invited projects!",
          icon: "error",
          timer: 2000,
        });
        return;
      }

      if (data.status === "success") {
        setNewInvite(data.projectCount);
      } else {
        Swal.fire({
          title: "Couldn't fetch invited projects!",
          icon: "error",
          timer: 2000,
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Couldn't fetch invited projects!",
        icon: "error",
        timer: 2000,
      });
    }
  };

  // Function to display compact sticky header on scroll
  useEffect(() => {
    const handleScroll = () => {
      const triggerHeight = 260; // adjust based on header height
      setShowStickyHeader(window.scrollY > triggerHeight);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // API to get expert experinece data
  // const getExpertExperience = async () => {
  //   const token = localStorage.getItem("AccessToken");
  //   const requestBody = {
  //     expertID: id,
  //   };
  //   try {
  //     let modifiedUrl = "fetch-expert-experience";
  //     const response = await fetch(`${API_URL}${modifiedUrl}`, {
  //       method: "POST",
  //       headers: {
  //         Authorization: `Bearer ${token}`,
  //         "Content-Type": "application/json",
  //       },
  //       body: JSON.stringify(requestBody),
  //     });

  //     const responseData = await response.json();
  //     if (responseData.status === "success") {
  //       setExperienceData(responseData.expertExperience);
  //       setHasExperince(true);
  //     } else if (responseData.status === "error") {
  //       console.error("Error fetching expert experience:", responseData.text);
  //     } else {
  //       console.error("Error fetching expert experience:", responseData.text);
  //     }
  //   } catch (error) {
  //     console.error("Network or unexpected error:", error);
  //   }
  // };

  // useEffect(() => {
  //   getExpertExperience();
  // }, [id]);

  // Api for saving profile name
  const saveNameData = async (name, link) => {
    const token = localStorage.getItem("AccessToken");
    fetch(`${API_URL}update-expert-name-data`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ expertID: id, name: name, link: link }),
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.status === "success") {
          setProfileName(name);
          setLinkedinURL(link);
          Swal.fire({
            title: "Updated",
            text: "Modification has been updated successfully!",
            icon: "success",
            showConfirmButton: false,
            timer: 1500,
          });
        } else {
          Swal.fire({
            title: "Error",
            text: "Failed to update bame and link!",
            icon: "error",
            showConfirmButton: false,
            timer: 1500,
          });
        }
      })
      .catch(() =>
        Swal.fire({
          title: "Error",
          text: "Error while updating!",
          icon: "error",
          showConfirmButton: false,
          timer: 1500,
        })
      );
  };

  function useBubbleCount() {
    const [count, setCount] = useState(30);

    useEffect(() => {
      const updateCount = () => {
        const width = window.innerWidth;

        // 1 dot per 45px, with a small minimum
        const bubbleCount = Math.max(8, Math.floor(width / 45));

        setCount(bubbleCount);
      };

      updateCount();
      window.addEventListener("resize", updateCount);

      return () => window.removeEventListener("resize", updateCount);
    }, []);

    return count;
  };

  const baseCount = useBubbleCount();
  const rowOffsets = [0, 2, 3];
  const getRowCount = (rowIndex) => {
    return Math.max(baseCount - (rowOffsets[rowIndex] || 0), 0);
  };

  return (
    <div className="bg-gray-100 min-h-screen">
      {/* Feedback widget scoped to ExpertDetails only */}
      <FeedbackWidget 
        name={profileName || data?.name} 
        email={data?.email} 
        topClass={showPwdWarning ? "top-14 sm:top-12" : "top-2"}
      />
      {!data ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-white/30 rounded-xl">
          <svg
            className="animate-spin h-20 w-20 text-gray-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="#365561"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="#365561"
              d="M4 12a8 8 0 018-8v8z"
            />
          </svg>
        </div>
      ) : (
        <>
          {/* Optional, user-initiated password change modal (dismissible) */}
          <ForcePasswordChangeModal
            isOpen={menuPwdOpen}
            expertID={id}
            dismissible
            onClose={() => setMenuPwdOpen(false)}
            onSuccess={() => {
              setMenuPwdOpen(false);
              setShowPwdWarning(false);
              setCount((c) => c + 1);
            }}
          />

          {/* Header with action buttons */}
          <AnimatePresence>
            {showMenuPanel && (
              <motion.div
                key="menu-panel"
                initial={{ y: -100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -100, opacity: 0 }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
                // className="fixed top-[78px] z-50 backdrop-blur-md bg-white/70 flex flex-wrap justify-end gap-2 w-full px-4 py-2 shadow-sm"
                className="fixed z-50 bg-white flex flex-wrap justify-end gap-2 w-full px-4 py-2 shadow-sm"
              >
                {/* Action Buttons */}
                <div className="flex flex-wrap justify-end items-center gap-2">
                  <DeleteExpert expertID={data?.id} />
                  <LogOut />
                  <button
                    onClick={() => setMenuPwdOpen(true)}
                    className="btn deleteButton"
                    title="Change Password"
                  >
                    <i className="fa fa-key" /> Change Password
                  </button>
                  {/* Separator */}
                  <div className="w-px h-6 bg-gray-300 mx-4" />
                  {/* Close Button */}
                  <button
                    onClick={() => setShowMenuPanel(false)}
                    className="text-gray-500 hover:text-black transition focus:outline-none border-none"
                  >
                    <i className="fa-solid fa-xmark text-xl"></i>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Compact sticky menubar on scroll down */}
          <AnimatePresence>
            {showStickyHeader && (
              <motion.div
                key="sticky-header"
                className="fixed left-0 w-full bg-white shadow z-50 overflow-hidden hidden md:block"
                initial={{ y: -60, opacity: 1 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -60, opacity: 1 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                style={{ height: "56px" }}
              >
                <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-2 flex justify-between items-center">
                  {/* Left: Avatar + Name */}
                  <div className="flex items-center gap-3">
                    <img
                      src={
                        data?.profilePicture?.includes(mediaLink)
                          ? data?.profilePicture
                          : `${databaseLink}${data?.profilePicture}`
                      }
                      onError={(e) => (e.target.src = expertDefaultImg)}
                      className="w-10 h-10 rounded-full object-cover border shadow-sm"
                      alt="Profile"
                    />
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <a
                          href={linkedinURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-md font-semibold text-gray-900 no-underline hover:no-underline"
                        >
                          {profileName}
                        </a>
                      </div>
                      {experienceData?.length > 0 && (
                        <p className="text-xs text-gray-600 mt-1 mb-1">
                          {experienceData[0]?.subExperienceBlocks?.length > 0
                            ? experienceData[0]?.subExperienceBlocks[0]
                                .designation
                            : experienceData[0]?.designation}{" "}
                          @ {experienceData[0]?.companyName}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Right: Menu */}
                  <div className="hidden md:flex flex-row gap-6 items-center relative">
                    {tabs.map((tab) => (
                      <button
                        key={tab.state}
                        onClick={() => setActiveTab(tab.state)}
                        className={`relative pb-2 text-sm font-medium ${
                          activeTab === tab.state
                            ? "text-[#365561] border-b-2 border-[#365561]"
                            : "text-gray-500"
                        } transition-colors duration-300 focus:outline-none`}
                      >
                        {tab.label === "Invitations" && newInvite ? (
                          <span className="absolute -top-2 -right-[16px] sm:-top-2 sm:-right-[18px] bg-red-600 text-white text-[10px] w-4 h-4 sm:w-5 sm:h-5 rounded-full font-semibold shadow flex items-center justify-center aspect-square">
                            {newInvite}
                          </span>
                        ) : null}
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Warning Banner for password change */}
          {showPwdWarning && (
            <div 
              className="bg-red-500 text-white text-center py-2 px-4 shadow-md cursor-pointer hover:bg-red-600 transition flex justify-center items-center gap-2 relative z-40"
              onClick={() => setMenuPwdOpen(true)}
            >
              <i className="fa-solid fa-triangle-exclamation"></i>
              <span className="text-sm sm:text-base">
                <strong>Action Required:</strong> For your security, please update your password. Click here to change it.
              </span>
            </div>
          )}

          {/* Menu bar with banner and profile picture */}
          <div className="relative bg-white shadow-sm z-40">

            {/* New header  */}
            {/* <div className="relative h-20 sm:h-28 md:h-32 lg:h-36 bg-cover bg-top bg-[24%_0%] sm:bg-[23%_0%] rounded-t-[10px] print-rounded-none">
              <div className="absolute inset-x-0 top-0 h-20 sm:h-28 md:h-32 lg:h-36 bg-gradient-to-r from-[#365561] via-[#2f4a55] to-[#0f172a]" />
              {[0, 1].map((row) => (
                <div
                  key={row}
                  className="absolute inset-0 flex flex-wrap justify-end gap-2 sm:gap-3 md:gap-4 px-4 opacity-80 top-[var(--mobile-top)] sm:top-[var(--sm)] md:top-[var(--md)] lg::top-[var(--lg)] xl:top-[var(--desktop-top)]"
                  style={{
                    "--mobile-top": `${24 + row * 22}px`,
                    "--sm": `${24 + row * 28}px`,
                    "--md": `${24 + row * 33}px`,
                    "--lg": `${24 + row * 38}px`,
                    "--desktop-top": `${24 + row * 42}px`,
                  }}
                >
                  {[...Array(getRowCount(row))].map((_, i) => {
                    const isHighlight = i % 7 === 0 || i % 11 === 0;

                    return (
                      <div
                        key={i}
                        className={`rounded-full h-[12px] w-[12px] sm:h-[13px] sm:w-[13px] md:h-[14px] md:w-[14px] lg:h-[15px] lg:w-[15px] xl:h-[16px] xl:w-[16px] ${
                          isHighlight ? "bg-[#2dd4bf]" : "bg-[#3b6a75]"
                        }`}
                      />
                    );
                  })}
                </div>
              ))}

              <div className="absolute top-4 left-4 text-white text-xl p-2 rounded-full transition-transform duration-200 ease-in-out">
                <img
                  src={logoWhite}
                  alt="EPH Logo"
                  className="w-[140px] sm:w-[160px] md:w-[190px] lg:w-[210px] xl:w-[240px] print-eph-logo"
                />
              </div>

              <button
                onClick={() => setShowMenuPanel(true)}
                className="absolute top-4 right-4 text-white text-xl mr-2 rounded-full transition-transform duration-200 ease-in-out hover:scale-125 drop-shadow-[0_0_6px_rgba(0,0,0,0.6)] focus:outline-none border-none"
                title="Menu"
              >
                <i className="fa-solid fa-bars"></i>
              </button>
              <div className="absolute bottom-2 right-4 cursor-pointer">
                <ProfileCompletionChart
                  profileData={data}
                  hasCV={hasCV}
                  hasExperience={hasExperience}
                  hasSkill={hasSkill}
                  hasIndustry={hasIndustry}
                  hasLanguage={hasLanguage}
                />
              </div>
            </div> */}

            {/* EPH banner */}
            <div
              className="relative h-40 sm:h-40 bg-cover bg-top"
              style={{ backgroundImage: `url(${bannerBack})` }}
            >
              <div className="absolute top-4 left-4 text-white text-xl p-2 rounded-full transition-transform duration-200 ease-in-out">
                <img
                  src={logoWhite}
                  alt="EPH Logo"
                  style={{ width: "180px" }}
                />
              </div>
              <button
                onClick={() => setShowMenuPanel(true)}
                className="absolute top-4 right-4 text-white text-xl mr-2 rounded-full transition-transform duration-200 ease-in-out hover:scale-125 drop-shadow-[0_0_6px_rgba(0,0,0,0.6)] focus:outline-none border-none"
                title="Menu"
              >
                <i className="fa-solid fa-bars"></i>
              </button>
              <div className="absolute bottom-2 right-4 cursor-pointer">
                <ProfileCompletionChart
                  profileData={data}
                  hasCV={hasCV}
                  hasExperience={hasExperience}
                  hasSkill={hasSkill}
                  hasIndustry={hasIndustry}
                  hasLanguage={hasLanguage}
                />
              </div>
            </div>

            {/* Profile picture and name */}
            <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8">
              <div className="relative flex flex-col items-left text-left -mt-20 sm:-mt-24">
                {/* Top part of the menu bar */}
                <div className="mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  {/* Left: Profile Picture */}
                  <div className="relative w-32 h-32 sm:w-36 sm:h-36 mt-3">
                    <ProfilePicture 
                      expertDefaultImg={expertDefaultImg}
                      profilePicturePreview={profilePicturePreview}
                      setProfilePicturePreview={setProfilePicturePreview}
                      expertID={id}
                    />
                    <div
                      className={`absolute top-9 -right-5 w-4 h-4 sm:w-5 sm:h-5 sm:top-12 sm:-right-6 rounded-full border-2 ${
                        isOnline === true
                          ? "bg-green-500 border-white"
                          : "bg-yellow-400 border-white"
                      }`}
                    >
                      <span className="text-gray-300 text-xs sm:text-sm font-medium px-2 py-0.5 rounded whitespace-nowrap absolute -top-1 left-2 sm:left-4">
                        {isOnline ? 'Online' : 'Offline'}
                      </span>
                    </div>
                  </div>
                  {/* Right: Company Logo */}
                  {experienceData?.length > 0 && (
                    <div className="flex flex-row gap-2 items-center sm:mt-20 sm:gap-3">
                      {experienceData[0]?.companyLogo ? (
                        <img
                          src={
                            experienceData[0]?.companyLogo.includes(
                              "/pictures/"
                            )
                              ? `https://images.expertpowerhouse.com${experienceData[0]?.companyLogo}`
                              : experienceData[0]?.companyLogo
                          }
                          alt="Company Logo"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = industryLogo;
                          }}
                          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-contain border"
                        />
                      ) : (
                        <img
                          src={industryLogo}
                          alt="Default Industry Logo"
                          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-contain border bg-gray-100"
                        />
                      )}
                      <p className="text-m text-gray-600 font-semibold">
                        {experienceData[0]?.companyName}
                      </p>
                    </div>
                  )}
                </div>

                {/* bottom part of menu bar */}
                <div className="mt-2 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  {/* Left: Name + Role*/}
                  <AnimatePresence mode="wait">
                    {isNameEditing ? (
                      <motion.div
                        key="edit-fields"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                        className="flex flex-col gap-1 w-1/2 p-[3px] rounded-md"
                      >
                        {/* Top right icons */}
                        <div className="relative top-1 h-[0px] flex justify-end gap-3">
                          <button
                            className="text-gray-500 hover:text-[#365561] transition focus:outline-none"
                            onClick={() => {
                              saveNameData(editedName, editedLinkedinURL);
                              setIsNameEditing(false);
                            }}
                            title="Save"
                          >
                            <i className="fa-solid fa-floppy-disk text-lg" />
                          </button>
                          <button
                            className="text-gray-500 hover:text-red-600 transition focus:outline-none"
                            onClick={() => setIsNameEditing(false)}
                            title="Cancel"
                          >
                            <i className="fa-solid fa-xmark text-lg" />
                          </button>
                        </div>

                        {/* Name Input */}
                        <input
                          type="text"
                          value={editedName}
                          onChange={(e) => setEditedName(e.target.value)}
                          placeholder="Full name"
                          className="border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none w-full text-sm sm:text-base"
                        />

                        {/* LinkedIn Input */}
                        <input
                          type="text"
                          value={editedLinkedinURL}
                          onChange={(e) => setEditedLinkedinURL(e.target.value)}
                          placeholder="LinkedIn URL"
                          className="border-b border-gray-300 text-blue-500 hover:border-[#365561] focus:border-[#365561] focus:outline-none w-full text-sm sm:text-base"
                        />
                      </motion.div>
                    ) : (
                      <motion.div
                        key="name-heading"
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -5 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="flex flex-col">
                          <div className="flex flex-wrap items-center gap-2">
                            <a
                              href={linkedinURL}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-2xl sm:text-3xl font-bold text-gray-900 no-underline hover:no-underline relative group"
                              onMouseEnter={() => {
                                const timer = setTimeout(
                                  () => setShowNameTooltip(true),
                                  2000
                                );
                                setNameTooltipTimer(timer);
                              }}
                              onMouseLeave={() => {
                                clearTimeout(nameTooltipTimer);
                                setShowNameTooltip(false);
                              }}
                            >
                              {profileName}

                              {/* Tooltip */}
                              <AnimatePresence>
                                {showNameTooltip && (
                                  <motion.div
                                    initial={{ opacity: 0, y: -5 }}
                                    animate={{ opacity: 1, y: -10 }}
                                    exit={{ opacity: 0, y: -5 }}
                                    transition={{ duration: 0.3 }}
                                    className="absolute bottom-full left-1/2 -translate-x-1/2 mt-3 z-50"
                                  >
                                    <div className="relative opacity-80 font-normal bg-black text-white text-sm px-1 py-2 w-[100px] rounded-tr-[10px] rounded-tl-[10px] rounded-br-[10px] shadow-md whitespace-nowrap text-wrap text-center">
                                      Click to view LinkedIn profile
                                      <svg
                                        className="absolute top-[60px] -left-6 w-12 h-8"
                                        viewBox="0 0 200 200"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <g transform="translate(0, 220) scale(1, -1)">
                                          <path
                                            d="
                                            M 100,10
                                            Q 100,100 200,120
                                            L 100,120
                                            Q 100,100 100,10
                                          "
                                            fill="black"
                                            stroke="black"
                                            strokeWidth="0"
                                            // opacity={0.7}
                                          />
                                        </g>
                                      </svg>
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </a>

                            <CV expertData={data} setHasCV={setHasCV} />
                            <button
                              className="relative text-gray-500 hover:text-black hover:bg-gray-200 focus:outline-none mt-0.5 px-1.5 py-0.3 rounded-full border transition"
                              onMouseEnter={() => setEditNameTooltip(true)}
                              onMouseLeave={() => setEditNameTooltip(false)}
                              onClick={() => {
                                setIsNameEditing(true);
                                setEditedName(profileName);
                                setEditedLinkedinURL(linkedinURL);
                                setEditNameTooltip(false);
                              }}
                            >
                              <i
                                className="fa-solid fa-pencil"
                                style={{ fontSize: "13px" }}
                              />
                              <AnimatePresence>
                                {editNameTooltip && (
                                  <motion.div
                                    className="absolute -top-7 -left-14 -translate-x-1/2 bg-[#365561] text-white text-xs px-2 py-1 rounded shadow-md whitespace-nowrap z-50 pointer-events-none"
                                    initial={{ opacity: 0, y: -20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 20 }}
                                    transition={{ duration: 0.3 }}
                                  >
                                    <span>Edit name and LinkedIn URL</span>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </button>
                          </div>
                          {experienceData?.length > 0 && (
                            <p className="text-sm text-gray-600 mt-1 mb-1">
                              {experienceData[0]?.subExperienceBlocks?.length >
                              0
                                ? experienceData[0]?.subExperienceBlocks[0]
                                    .designation
                                : experienceData[0]?.designation}{" "}
                              @ {experienceData[0]?.companyName}
                            </p>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  {/* Right: Action buttons or menu bar */}
                  <div className="relative flex gap-6">
                    {tabs.map((tab) => (
                      <div key={tab.state} className="relative group">
                        <button
                          onClick={() => setActiveTab(tab.state)}
                          className={`relative pb-2 text-sm font-medium ${
                            activeTab === tab.state
                              ? "text-[#365561]"
                              : "text-gray-500"
                          } transition-colors duration-300 focus:outline-none`}
                        >
                          {/* Notification Badge */}
                          {tab.label === "Invitations" && newInvite ? (
                            <span className="absolute -top-2 -right-[16px] sm:-top-2 sm:-right-[18px] bg-red-600 text-white text-[12px] w-4 h-4 sm:w-5 sm:h-5 rounded-full font-semibold shadow flex items-center justify-center aspect-square">
                              {newInvite}
                            </span>
                          ) : null}

                          {/* Label */}
                          {tab.label}
                          {activeTab === tab.state && (
                            <motion.div
                              layoutId="underline"
                              className="absolute bottom-0 left-0 right-0 h-[3px] bg-[#365561]"
                              transition={{
                                type: "spring",
                                stiffness: 500,
                                damping: 30,
                              }}
                            />
                          )}
                        </button>

                        {/* Tooltip */}
                        <div className="absolute bottom-12 left-14 -translate-x-1/2 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-80 transition-opacity duration-400 pointer-events-none whitespace-nowrap z-10">
                          {tab.label === "My Profile" && (
                            <div className="relative font-normal bg-black text-white text-sm px-1 py-2 w-[100px] rounded-tr-[10px] rounded-tl-[10px] rounded-br-[10px] shadow-md whitespace-nowrap text-wrap text-center">
                              Click to view or modify your profile
                              <svg
                                className="absolute top-[60px] -left-6 w-12 h-8"
                                viewBox="0 0 200 200"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <g transform="translate(0, 220) scale(1, -1)">
                                  <path
                                    d="
                                    M 100,10
                                    Q 100,100 200,120
                                    L 100,120
                                    Q 100,100 100,10
                                  "
                                    fill="black"
                                    stroke="black"
                                    strokeWidth="0"
                                  />
                                </g>
                              </svg>
                            </div>
                          )}
                          {tab.label === "My Projects" && (
                            <div className="relative font-normal bg-black text-white text-sm px-1 py-2 w-[100px] rounded-tr-[10px] rounded-tl-[10px] rounded-br-[10px] shadow-md whitespace-nowrap text-wrap text-center">
                              Click to view projects you are involved
                              <svg
                                className="absolute top-[60px] -left-6 w-12 h-8"
                                viewBox="0 0 200 200"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <g transform="translate(0, 220) scale(1, -1)">
                                  <path
                                    d="
                                    M 100,10
                                    Q 100,100 200,120
                                    L 100,120
                                    Q 100,100 100,10
                                  "
                                    fill="black"
                                    stroke="black"
                                    strokeWidth="0"
                                  />
                                </g>
                              </svg>
                            </div>
                          )}
                          {tab.label === "Invitations" && (
                            <div className="relative font-normal bg-black text-white text-sm px-1 py-2 w-[100px] rounded-tr-[10px] rounded-tl-[10px] rounded-br-[10px] shadow-md whitespace-nowrap text-wrap text-center">
                              Click to review project invitations
                              <svg
                                className="absolute top-[60px] -left-6 w-12 h-8"
                                viewBox="0 0 200 200"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <g transform="translate(0, 220) scale(1, -1)">
                                  <path
                                    d="
                                    M 100,10
                                    Q 100,100 200,120
                                    L 100,120
                                    Q 100,100 100,10
                                  "
                                    fill="black"
                                    stroke="black"
                                    strokeWidth="0"
                                  />
                                </g>
                              </svg>
                            </div>
                          )}
                          {tab.label === "Projects" && (
                            <div className="relative font-normal bg-black text-white text-sm -ml-[105px] px-1 py-2 w-[100px] rounded-tr-[5px] rounded-tl-[5px] rounded-bl-[5px] shadow-md whitespace-nowrap text-wrap text-center">
                              Click to see available projects
                              <svg
                                className="absolute top-[60px] left-[75px] w-12 h-8"
                                viewBox="0 0 200 200"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <g transform="translate(206, 220) scale(-1, -1)">
                                  <path
                                    d="
                                    M 100,10
                                    Q 100,100 200,120
                                    L 100,120
                                    Q 100,100 100,10
                                  "
                                    fill="black"
                                    stroke="black"
                                    strokeWidth="0"
                                  />
                                </g>
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main content area */}
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 mt-4 sm:mt-36 pb-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
            {activeTab === "profile" && (
              <ProfilePage
                data={data}
                expertID={id}
                experienceData={experienceData}
                setExperienceData={setExperienceData}
                setHasExperince={setHasExperince}
                setHasSkill={setHasSkill}
                setHasIndustry={setHasIndustry}
                setHasLanguage={setHasLanguage}
                showStickyHeader={showStickyHeader}
              />
            )}
            {activeTab === "projects" && (
              <ProjectsPage expertID={id} showStickyHeader={showStickyHeader} />
            )}
            {activeTab === "invitation" && (
              <InvitationsPage
                expertID={id}
                getCount={fetchProjects}
                showStickyHeader={showStickyHeader}
              />
            )}
            {activeTab === "myProjects" && (
              <MyProjectPage
                expertID={id}
                showStickyHeader={showStickyHeader}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
